import React, { useEffect, useState } from 'react';
import { Alert, Button, DatePicker, Divider, Form, Input, InputNumber, Select, Space, Spin, Switch, Tag, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { useOnCallQuery, useUpdateOnCallMutation } from '../../queries/settings';
import { US_TIMEZONES } from '../../utils/timezones';
import { formatPhone } from '../../utils/callLog';
import { Card, CardHead } from '../../components';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((label, value) => ({ label, value }));

const toForm = (oc = {}) => ({
  enabled: !!oc.enabled,
  timezone: oc.timezone || 'America/Chicago',
  people: oc.people || [],
  weekStartDay: oc.rotation?.weekStartDay ?? 5,
  schedule: (oc.rotation?.schedule || []).map((s) => ({ weekStart: s.weekStart ? dayjs(s.weekStart) : null, onCall: s.onCall, off: s.off })),
  responseWindowMinutes: oc.escalation?.responseWindowMinutes ?? 60,
  tiers: (oc.escalation?.tiers || []).map((t) => ({ people: t.people || [] }))
});

const toPayload = (v) => ({
  enabled: !!v.enabled,
  timezone: v.timezone,
  people: (v.people || []).filter((p) => p?.name?.trim()),
  rotation: {
    weekStartDay: v.weekStartDay ?? 5,
    schedule: (v.schedule || [])
      .filter((s) => s?.weekStart && s?.onCall)
      .map((s) => ({ weekStart: dayjs(s.weekStart).format('YYYY-MM-DD'), onCall: s.onCall, off: s.off || '' }))
  },
  escalation: {
    responseWindowMinutes: v.responseWindowMinutes ?? 60,
    tiers: (v.tiers || []).map((t) => ({ people: t?.people || [] })).filter((t) => t.people.length)
  }
});

const Remove = ({ onClick }) => (
  <Button type="text" size="small" aria-label="Remove" icon={<FontAwesomeIcon icon={faXmark} />} onClick={onClick} />
);

function Status({ current, plan }) {
  return (
    <Alert
      style={{ marginBottom: 16 }}
      type={current ? 'info' : 'warning'}
      showIcon
      message={
        current ? (
          <span>
            On call now: <strong>{current.name}</strong>
            {current.phone ? ` · ${formatPhone(current.phone)}` : ''}
          </span>
        ) : (
          'No one is on call this week. Add a week to the rotation below.'
        )
      }
      description={
        plan?.length > 1 ? (
          <Space wrap size={4}>
            Escalates to:
            {plan.slice(1).map((t, i) => (
              <Tag key={i}>{(t.people || []).map((p) => p.name).join(' / ')}</Tag>
            ))}
          </Space>
        ) : null
      }
    />
  );
}

export default function OnCallCard() {
  const [form] = Form.useForm();
  const { data, isPending } = useOnCallQuery();
  const { mutate, isPending: saving } = useUpdateOnCallMutation();
  const enabled = Form.useWatch('enabled', form);
  const people = Form.useWatch('people', form) || [];
  const [ready, setReady] = useState(false);
  const names = people.filter((p) => p?.name).map((p) => ({ label: p.name, value: p.name }));

  useEffect(() => {
    if (!data) return;
    form.setFieldsValue(toForm(data.onCall));
    setReady(true);
  }, [data, form]);

  return (
    <Card aria-labelledby="oncall-title">
      <CardHead>
        <h2 id="oncall-title">On-call</h2>
        <span>who gets paged when the assistant flags an emergency</span>
      </CardHead>
      {isPending && !ready ? (
        <Spin />
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => mutate(toPayload(v))} initialValues={toForm()}>
          {enabled && <Status current={data?.current} plan={data?.plan} />}
          <Form.Item name="enabled" label="On-call enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          {enabled && (
            <>
              <Form.Item name="timezone" label="Timezone" style={{ maxWidth: 240 }}>
                <Select options={US_TIMEZONES} />
              </Form.Item>

              <Divider orientation="left" plain>
                Roster
              </Divider>
              <Form.List name="people">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <Space key={key} align="baseline" wrap style={{ display: 'flex', marginBottom: 8 }}>
                        <Form.Item name={[name, 'name']} rules={[{ required: true, message: 'Name' }]} style={{ marginBottom: 0, width: 150 }}>
                          <Input placeholder="Name" />
                        </Form.Item>
                        <Form.Item
                          name={[name, 'phone']}
                          rules={[{ pattern: /^\+[1-9]\d{7,14}$/, message: 'Use +1…' }]}
                          style={{ marginBottom: 0, width: 160 }}
                        >
                          <Input placeholder="+18175551234" />
                        </Form.Item>
                        <Form.Item name={[name, 'email']} rules={[{ type: 'email' }]} style={{ marginBottom: 0, width: 220 }}>
                          <Input placeholder="email@company.com" />
                        </Form.Item>
                        <Remove onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<FontAwesomeIcon icon={faPlus} />}>
                      Add person
                    </Button>
                  </>
                )}
              </Form.List>

              <Divider orientation="left" plain>
                Weekly rotation
              </Divider>
              <Form.Item name="weekStartDay" label="Week rolls over on" style={{ maxWidth: 220 }}>
                <Select options={WEEKDAYS} />
              </Form.Item>
              <Form.List name="schedule">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <Space key={key} align="baseline" wrap style={{ display: 'flex', marginBottom: 8 }}>
                        <Form.Item name={[name, 'weekStart']} style={{ marginBottom: 0 }}>
                          <DatePicker placeholder="Week start" />
                        </Form.Item>
                        <Form.Item name={[name, 'onCall']} style={{ marginBottom: 0, width: 150 }}>
                          <Select placeholder="On call" options={names} />
                        </Form.Item>
                        <Form.Item name={[name, 'off']} style={{ marginBottom: 0, width: 150 }}>
                          <Select allowClear placeholder="Off (optional)" options={names} />
                        </Form.Item>
                        <Remove onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<FontAwesomeIcon icon={faPlus} />}>
                      Add week
                    </Button>
                  </>
                )}
              </Form.List>

              <Divider orientation="left" plain>
                Escalation
              </Divider>
              <Form.Item name="responseWindowMinutes" label="Minutes to respond before escalating" style={{ maxWidth: 320 }}>
                <InputNumber min={1} max={720} style={{ width: 120 }} />
              </Form.Item>
              <Typography.Paragraph type="secondary">
                The on-call person is paged first. Add backup tiers in the order they should be paged.
              </Typography.Paragraph>
              <Form.List name="tiers">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                        <Form.Item name={[name, 'people']} style={{ marginBottom: 0, width: 360 }}>
                          <Select mode="multiple" allowClear placeholder="People in this tier" options={names} />
                        </Form.Item>
                        <Remove onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<FontAwesomeIcon icon={faPlus} />}>
                      Add backup tier
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}
          <Divider />
          <Button type="primary" htmlType="submit" loading={saving}>
            Save on-call
          </Button>
        </Form>
      )}
    </Card>
  );
}
